import { FormEvent, useEffect, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { Drawer } from './Drawer';
import { Field, Select, TextInput } from './Field';
import { RecordingScheduleGrid } from './RecordingScheduleGrid';
import { camerasApi } from '@api/cameras';
import { HttpError } from '@api/http';
import type {
  Camera,
  CameraGroup,
  CameraStatus,
  CameraType,
  RecordingMode,
  RecordingScheduleSlots,
  StorageTarget,
} from '@api/types';
import { createEmptyRecordingScheduleSlots } from '@lib/recording-schedule';

interface Props {
  open: boolean;
  camera: Camera | null;
  groups: CameraGroup[];
  onClose: () => void;
  onSaved: (camera: Camera) => void;
}

interface SettingsForm {
  name: string;
  rtspUrl: string;
  recordingMode: RecordingMode;
  recordingEnabled: boolean;
  storageTarget: StorageTarget;
  status: CameraStatus;
  type: CameraType;
  groupId: string;
  isFavorite: boolean;
}

const RTSP_RE = /^rtsps?:\/\/.+/i;
const NAME_MAX = 100;

type FieldErrors = Partial<Record<keyof SettingsForm, string>>;

const DEFAULT_FORM: SettingsForm = {
  name: '',
  rtspUrl: '',
  recordingMode: 'continuous',
  recordingEnabled: true,
  storageTarget: 'local',
  status: 'active',
  type: 'standard',
  groupId: '',
  isFavorite: false,
};

const browserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';

export function CameraSettingsDrawer({
  open,
  camera,
  groups,
  onClose,
  onSaved,
}: Props): JSX.Element | null {
  const [form, setForm] = useState<SettingsForm>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleSlots, setScheduleSlots] = useState<RecordingScheduleSlots>(() =>
    createEmptyRecordingScheduleSlots(),
  );
  const [scheduleTimezone, setScheduleTimezone] = useState<string>('');
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleLoadError, setScheduleLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !camera) return;
    setForm({
      name: camera.name,
      rtspUrl: camera.rtspUrl,
      recordingMode: camera.recordingMode === 'manual' ? 'continuous' : camera.recordingMode,
      recordingEnabled: camera.recordingEnabled,
      storageTarget: camera.storageTarget === 'nas' ? 'local' : camera.storageTarget,
      status: camera.status,
      type: camera.type,
      groupId: camera.groupId == null ? '' : String(camera.groupId),
      isFavorite: camera.isFavorite,
    });
    setErrors({});
    setSubmitError(null);
    setSubmitting(false);
    setScheduleLoadError(null);
  }, [open, camera]);

  useEffect(() => {
    if (!open || !camera || !form.recordingEnabled || form.recordingMode !== 'scheduled') return;
    const ac = new AbortController();
    setScheduleLoading(true);
    setScheduleLoadError(null);
    void camerasApi
      .getRecordingSchedule(camera.id, ac.signal)
      .then((res) => {
        setScheduleTimezone(res.timezone);
        setScheduleSlots(res.slots ?? createEmptyRecordingScheduleSlots());
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err)) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load recording schedule';
        setScheduleLoadError(message);
        setScheduleTimezone(browserTimeZone());
        setScheduleSlots(createEmptyRecordingScheduleSlots());
      })
      .finally(() => {
        if (!ac.signal.aborted) setScheduleLoading(false);
      });
    return () => ac.abort();
  }, [open, camera, form.recordingEnabled, form.recordingMode]);

  if (!open || !camera) return null;

  const update = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const name = form.name.trim();
    if (!name) next.name = 'Name is required';
    else if (name.length > NAME_MAX) next.name = `Name must be ≤ ${NAME_MAX} characters`;

    const rtsp = form.rtspUrl.trim();
    if (!rtsp) next.rtspUrl = 'RTSP URL is required';
    else if (!RTSP_RE.test(rtsp)) next.rtspUrl = 'Must start with rtsp:// or rtsps://';

    return next;
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const saved = await camerasApi.update(camera.id, {
        name: form.name.trim(),
        rtspUrl: form.rtspUrl.trim(),
        recordingMode: form.recordingMode,
        recordingEnabled: form.recordingEnabled,
        storageTarget: form.storageTarget,
        status: form.status,
        type: form.type,
        groupId: form.groupId === '' ? null : Number(form.groupId),
        isFavorite: form.isFavorite,
      });
      if (form.recordingEnabled && form.recordingMode === 'scheduled') {
        try {
          await camerasApi.putRecordingSchedule(camera.id, { slots: scheduleSlots });
        } catch (err) {
          const message =
            err instanceof HttpError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Failed to save recording schedule';
          setSubmitError(
            `Settings were saved, but the weekly schedule could not be saved: ${message}`,
          );
          return;
        }
      }
      onSaved(saved);
    } catch (err) {
      const message =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to save settings';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const drawerWidth =
    form.recordingEnabled && form.recordingMode === 'scheduled'
      ? 'w-full max-w-4xl sm:max-w-4xl'
      : 'w-full max-w-md';

  return (
    <Drawer
      open={open}
      title={`Settings — ${camera.name}`}
      description="Connection, recording, storage, and operational options. Updating the RTSP URL re-syncs the MediaMTX path automatically."
      onClose={submitting ? () => undefined : onClose}
      widthClass={drawerWidth}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-muted">
            {submitting ? 'Saving…' : 'All fields marked * are required.'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={clsx(
                'rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-foreground',
                'hover:border-accent disabled:opacity-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="camera-settings-form"
              disabled={submitting || scheduleLoading}
              className={clsx(
                'rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white',
                'hover:bg-accent-hover disabled:opacity-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              {submitting ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>
      }
    >
      <form id="camera-settings-form" className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          >
            {submitError}
          </p>
        ) : null}

        <Field label="Name" required error={errors.name}>
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              maxLength={NAME_MAX}
              placeholder="Front Door"
              invalid={Boolean(errors.name)}
              disabled={submitting}
            />
          )}
        </Field>

        <Field
          label="RTSP URL"
          required
          hint="Example: rtsp://user:pass@192.168.1.10:554/Streaming/Channels/101"
          error={errors.rtspUrl}
        >
          {(id) => (
            <TextInput
              id={id}
              value={form.rtspUrl}
              onChange={(e) => update('rtspUrl', e.target.value)}
              placeholder="rtsp://…"
              invalid={Boolean(errors.rtspUrl)}
              disabled={submitting}
              spellCheck={false}
            />
          )}
        </Field>

        <Field label="Group">
          {(id) => (
            <Select
              id={id}
              value={form.groupId}
              onChange={(e) => update('groupId', e.target.value)}
              disabled={submitting}
            >
              <option value="">— No group —</option>
              {groups.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {g.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Camera type">
          {(id) => (
            <Select
              id={id}
              value={form.type}
              onChange={(e) => update('type', e.target.value as CameraType)}
              disabled={submitting}
            >
              <option value="standard">Standard</option>
              <option value="ptz">PTZ</option>
              <option value="360">360°</option>
            </Select>
          )}
        </Field>

        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            checked={form.isFavorite}
            onChange={(e) => update('isFavorite', e.target.checked)}
            disabled={submitting}
            className="h-4 w-4 rounded border-border bg-surface-2 accent-accent"
          />
          Mark as favorite
        </label>

        <hr className="border-border" />

        <Field
          label="Recording"
          hint="Turns recording ON/OFF for this camera. When OFF, no recording type or schedule is applied."
        >
          {(id) => (
            <Select
              id={id}
              value={form.recordingEnabled ? 'on' : 'off'}
              onChange={(e) => update('recordingEnabled', e.target.value === 'on')}
              disabled={submitting}
            >
              <option value="off">Off</option>
              <option value="on">On</option>
            </Select>
          )}
        </Field>

        {form.recordingEnabled ? (
          <>
            <Field
              label="Recording mode"
              hint="Continuous records 24/7 when active. Scheduled uses the weekly grid below."
            >
              {(id) => (
                <Select
                  id={id}
                  value={form.recordingMode}
                  onChange={(e) => update('recordingMode', e.target.value as RecordingMode)}
                  disabled={submitting}
                >
                  <option value="continuous">Continuous (24/7)</option>
                  <option value="scheduled">Scheduled</option>
                </Select>
              )}
            </Field>

            {form.recordingMode === 'scheduled' ? (
              <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-2/50 p-3">
                <p className="text-xs font-medium text-foreground">Weekly recording schedule</p>
                {scheduleLoadError ? (
                  <p className="text-xs text-danger">{scheduleLoadError}</p>
                ) : null}
                {scheduleLoading ? (
                  <p className="text-xs text-muted">Loading schedule…</p>
                ) : (
                  <>
                    <p className="text-[11px] leading-relaxed text-muted">
                      Slot times use the IANA zone{' '}
                      <span className="font-mono text-foreground">{scheduleTimezone || '…'}</span>. If none is stored
                      for this camera, the server uses{' '}
                      <span className="font-mono">DEFAULT_RECORDING_SCHEDULE_TIMEZONE</span> (default{' '}
                      <span className="font-mono">Asia/Kolkata</span>).
                    </p>
                    <RecordingScheduleGrid value={scheduleSlots} onChange={setScheduleSlots} />
                  </>
                )}
              </div>
            ) : null}
          </>
        ) : null}

        <Field
          label="Storage target"
          hint="Saved on the camera for retention and policy modules. MediaMTX record path is configured separately on the server."
        >
          {(id) => (
            <Select
              id={id}
              value={form.storageTarget}
              onChange={(e) => update('storageTarget', e.target.value as StorageTarget)}
              disabled={submitting}
            >
              {/* <option value="nas">NAS</option> */}
              <option value="local">Local</option>
            </Select>
          )}
        </Field>

        <Field label="Stream status" hint="Inactive stops pulling RTSP and disables dashboard preview.">
          {(id) => (
            <Select
              id={id}
              value={form.status}
              onChange={(e) => update('status', e.target.value as CameraStatus)}
              disabled={submitting}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          )}
        </Field>
      </form>
    </Drawer>
  );
}
